'use client'

import { memo, useCallback, useEffect, useState, type FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import type { Action, DynamicActionsProps, ActionParams, FetchMethod } from '../types'
import { DynamicFields } from './DynamicFields'
import { splitFieldErrors } from '../lib/fieldErrors'
import { buildDefaults, buildSubmitPayload, toRequestBody } from '../lib/formHelper'
import { dynamicClient } from '@components/DynamicActions/hooks/useActionParams'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@fp/ui-kit'
import { Button, type ButtonProps, Spin } from '@fp/ui-kit'
import { emitToast } from '@lib/emitToast'

export const breakWords = {
  wordBreak: 'break-word' as const,
}

interface FormDialogProps {
  action: Action
  params?: ActionParams
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  onSuccess: DynamicActionsProps['onSuccess']
  onDynamicActionSelect: (action: Action) => () => void
}

export const FormDialog: FC<FormDialogProps> = memo(
  ({ action, params, isOpen, setIsOpen, onSuccess, onDynamicActionSelect }) => {
    const [isLoading, setIsLoading] = useState(false)
    // Backend errors for keys this form has no input for: `base`, or a field the action never rendered.
    const [formError, setFormError] = useState('')
    const formMethods = useForm({
      mode: 'onChange',
    })

    useEffect(() => {
      formMethods.reset(buildDefaults(params?.fields))
    }, [formMethods, params?.fields])

    const {
      control,
      handleSubmit,
      formState: { isValid },
      reset,
      setError,
    } = formMethods

    const onClose = useCallback(() => {
      reset()
      setFormError('')
      setIsOpen(false)
    }, [setIsOpen, reset])

    const onSubmit = useCallback(
      async (data: Record<string, unknown>) => {
        if (!action.path) {
          emitToast({
            message: 'Action error',
            details: [{ message: 'No action.path data' }],
          })

          return
        }

        setFormError('')
        setIsLoading(true)
        const res = await dynamicClient(
          action.method as FetchMethod,
          action.path,
          toRequestBody(buildSubmitPayload(params?.fields, data)),
          action.response_content_type,
        )
        setIsLoading(false)

        // A rejected submit keeps the dialog open so the picked file and the errors survive, and
        // leaves `onSuccess` uncalled so callers never refetch for a change that did not happen.
        if (res.type === 'error') {
          const { fields, formMessages } = splitFieldErrors(
            res.fieldErrors ?? {},
            (params?.fields ?? []).map((field) => field.param_key || ''),
          )

          fields.forEach(([name, message]) => setError(name, { type: 'server', message }))

          setFormError(formMessages.join(' '))

          return
        }

        onSuccess?.(res, action)
        onClose()
      },
      [action, params?.fields, onSuccess, onClose, setError],
    )

    const onButtonClick = useCallback(
      ({ action }: ActionParams['buttons'][0]) =>
        () =>
          action ? onDynamicActionSelect(action)() : console.error('No action on onClick'),
      [onDynamicActionSelect],
    )

    if (typeof params === 'undefined')
      return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent
            size='md'
            className='max-h-[90vh] overflow-y-auto max-w-full flex justify-center items-center min-h-[200px]'
          >
            <DialogTitle className='sr-only'>{action.name ?? 'Loading'}</DialogTitle>
            <Spin size='xl' />
          </DialogContent>
        </Dialog>
      )

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          size='md'
          aria-describedby={params?.modal_title}
          className='max-h-[90vh] overflow-y-auto max-w-full'
          style={breakWords}
        >
          <DialogHeader>
            <DialogTitle className='text-center'>{params?.modal_title}</DialogTitle>
          </DialogHeader>

          <p className='text-center mb-4'>{params?.modal_description}</p>

          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className='grid gap-4'>
              <DynamicFields fields={params?.fields} control={control} />

              {formError && (
                <p role='alert' className='text-center text-xs text-destructive-foreground'>
                  {formError}
                </p>
              )}

              <div className='flex justify-center gap-2 mt-4'>
                {params?.buttons?.map((button) => {
                  const buttonProps: ButtonProps =
                    button.type === 'submit'
                      ? { type: 'submit', variant: 'primary', disabled: !isValid || isLoading, isLoading }
                      : {
                          type: 'button',
                          variant: 'outline',
                          onClick: onButtonClick(button),
                        }

                  return (
                    <Button {...buttonProps} key={button.name} className='max-w-60 overflow-hidden'>
                      {button.name}
                    </Button>
                  )
                })}
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    )
  },
)
